<template>
  <div>
    <slot :toggle-modal="() => (showModal = true)" />
    <lf-modal v-if="showModal" :close="closeModal" :title="title">
      <template #content>
        <form data-cy="attribute-base-form" @submit.prevent="submit">
          <div class="px-6 py-2">
            <lf-input
              class="-mb-0"
              name="name"
              :placeholder="$t('COMMON.NAME')"
              :value="attribute?.name"
            />
          </div>
          <div class="px-6 py-2">
            <lf-textarea
              class="w-full rounded-sm border-gray-200 text-xs text-gray-600 placeholder-gray-400"
              :placeholder="$t('COMMON.DESCRIPTION')"
              rows="4"
              name="description"
              :character-limit="TEXT_CHARACTER_LIMIT_DEFAULT"
              :value="attribute?.description || ''"
            />
          </div>
          <div v-if="isTemplateOffer" class="px-6 py-2">
            <lf-dropdown
              name="product_type"
              :placeholder="$t('INTEGRATIONS.PRODUCT_TYPE')"
              :options="productTypes"
            />
          </div>
          <div v-else class="flex flex-col space-y-4 px-6 py-2">
            <!--Remove once we remove old attribute builder-->
            <div v-if="!isEditMode" class="flex justify-end space-x-8">
              <div class="flex">
                <div>{{ $t("ATTRIBUTE_BUILDER.STATIC") }}</div>
                <lf-switch
                  v-model="formulaAttributeType"
                  class="px-2"
                  name="version-control"
                  data-cy="type-toggle"
                />
                <span>{{ $t("ATTRIBUTE_BUILDER.FORMULA") }}</span>
              </div>
              <div class="flex">
                <div>{{ $t("COMMON.LEGACY") }}</div>
                <lf-switch
                  v-model="createV2Attribute"
                  :disabled="forceNew"
                  class="px-2"
                  name="version-control"
                  data-cy="v2-toggle"
                />
                <span>{{ $t("COMMON.NEW") }}</span>
              </div>
            </div>
          </div>
          <hr class="my-6" />
          <div class="flex justify-end px-6 pb-6">
            <outline-button class="mr-3" @click="closeModal">
              {{ $t("COMMON.CANCEL") }}
            </outline-button>
            <primary-button
              type="submit"
              :disabled="saving"
              data-cy="continue-button"
            >
              {{ $t("COMMON.CONTINUE") }}
            </primary-button>
          </div>
        </form>
      </template>
    </lf-modal>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { useForm } from "vee-validate";

import {
  CustomAttributeStatusOption,
  CustomAttributeTypeOption,
} from "@/enums/customAttributes";
import {
  CUSTOM_ATTRIBUTE_SCHEMA_VERSION,
  TEXT_CHARACTER_LIMIT_DEFAULT,
} from "@/helpers/constants";
import { callStoreAction } from "@/helpers/vee-validate";
import { useAttributeBuilderStore } from "@/stores/customAttributeBuilder";

import LfModal from "@/components/ui/Modal.vue";
import LfDropdown from "@/components/ui/inputs/LfDropdown.vue";

import type { CustomAttribute } from "@/models/customAttributes/customAttribute";
import type { PropType } from "vue";

const emit = defineEmits<{
  "attribute:saved": [customAttribute: CustomAttribute];
}>();

const props = defineProps({
  attribute: {
    type: Object as PropType<CustomAttribute | null>,
    default: () => ({
      name: "",
      content: {
        version: CUSTOM_ATTRIBUTE_SCHEMA_VERSION,
        formula: [],
      },
      description: "",
      status: CustomAttributeStatusOption.draft,
      type: CustomAttributeTypeOption.formula,
    }),
  },
  isTemplateOffer: {
    type: Boolean,
    default: false,
  },
  forceNew: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const { getters } = useStore();
const attributesStore = useAttributeBuilderStore();

const showModal = ref(false);
const saving = ref(false);
const createV2Attribute = ref(true);
const formulaAttributeType = ref(
  props.attribute?.type === CustomAttributeTypeOption.formula
);

const { handleSubmit } = useForm({
  initialValues: {
    ...props.attribute,
  },
});

const productTypes = computed<Record<string, string>>(
  () => getters["options/productTypes"]
);
const attributeBuilderLatestSchemaVersion = computed<string>(
  () => getters["options/attributeSchemaVersion"]
);

const isEditMode = computed(() => Boolean(props.attribute?.id));

const title = computed(() => {
  const mode = isEditMode.value
    ? t("COMMON.EDIT")
    : `${t("COMMON.CREATE")} ${t("COMMON.NEW")}`;
  const attributeTypeLabel = props.isTemplateOffer
    ? t("ATTRIBUTE_BUILDER.OFFER_TEMPLATE")
    : t("ATTRIBUTE_BUILDER.ATTRIBUTE");
  return `${mode} ${attributeTypeLabel}`;
});

const closeModal = () => {
  showModal.value = false;
};

const submit = handleSubmit(async (values, actions) => {
  if (saving.value) {
    return;
  }

  const emptyStateV2 = {
    version: attributeBuilderLatestSchemaVersion.value,
    expression: undefined,
  };
  const type = formulaAttributeType.value
    ? CustomAttributeTypeOption.formula
    : CustomAttributeTypeOption.api;
  const status = formulaAttributeType.value
    ? CustomAttributeStatusOption.draft
    : CustomAttributeStatusOption.published;
  try {
    saving.value = true;
    const payload: Partial<CustomAttribute> = {
      ...props.attribute,
      ...values,
      type,
      content:
        (props.forceNew || createV2Attribute.value) && !isEditMode.value
          ? emptyStateV2
          : props.attribute?.content,
      ...((props.forceNew || createV2Attribute.value) && {
        tokens: props.attribute?.tokens || [],
      }),
    };
    if (
      (isEditMode.value &&
        props.attribute?.status !== CustomAttributeStatusOption.published) ||
      !formulaAttributeType.value
    ) {
      payload.status = status;
    }
    const actionName = isEditMode.value ? "updateAttribute" : "createAttribute";

    const savedAttribute: CustomAttribute = await callStoreAction(
      payload,
      actions,
      actionName,
      attributesStore
    );

    if (savedAttribute?.id) {
      emit("attribute:saved", savedAttribute);
      closeModal();
    }
  } finally {
    saving.value = false;
  }
});
</script>
